import './AudioAbbreviations.scss';

import React from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  Pagination,
  Row,
  Table,
} from 'react-bootstrap';

import DropDown from '../../commonComponents/DropDown/DropDown';
import Spinner from '../../commonComponents/Spinner/Spinner';
import { isCsvFile, parseCsvFile } from '../../utilities/questionExport';
import { AudioDecorator } from '../Questions/commonComponents/QuestionAudioActions/QuestionAudioActions';

const ABBREVIATIONS_TYPES = [
  { text: 'Free text', value: 'FREE_TEXT' },
  { text: 'Spelled out', value: 'SPELLED_OUT' },
  { text: 'Phonemes (X-SAMPA)', value: 'PHONEME' },
];

const EMPTY_ITEM = {
  text: '',
  replacement: '',
  type: ABBREVIATIONS_TYPES[0].value,
};

export default class AudioAbbreviations extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      newItem: { ...EMPTY_ITEM },
      selectedItem: { ...EMPTY_ITEM },
      isEditing: false,
      showMoveToNewOrderIdx: false,
      search: '',
      currentPage: 1,
      parsedFile: null,
    };

    this.audio = AudioDecorator.getInstance();
    this.inputFileRef = React.createRef();
    this.searchTimeoutId = null;
  }

  componentDidMount() {
    // Load initial data
    this.loadAudioAbbreviations();
  }

  componentDidUpdate(prevProps, prevState) {
    // Handle search changes with debounce
    if (prevState.search !== this.state.search) {
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
      }
      this.searchTimeoutId = setTimeout(() => {
        this.setState({ currentPage: 1 });
        this.loadAudioAbbreviations(this.state.search, 1);
      }, 300);
    }

    // Update current page when pagination changes from external updates
    if (
      prevProps.audioAbbreviations?.pagination?.currentPage !==
        this.props.audioAbbreviations?.pagination?.currentPage &&
      this.props.audioAbbreviations?.pagination?.currentPage &&
      this.props.audioAbbreviations.pagination.currentPage !==
        this.state.currentPage
    ) {
      this.setState({
        currentPage: this.props.audioAbbreviations.pagination.currentPage,
      });
    }
  }

  componentWillUnmount() {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
    }
    // Clear data when component unmounts
    this.props.clearAudioAbbreviations();
  }

  loadAudioAbbreviations = (search = '', page = 1) => {
    const limit =
      this.props.audioAbbreviations?.pagination?.itemsPerPage || 100;
    const skip = (page - 1) * limit;
    this.props.getAllAudioAbbreviations(search, page, skip, limit);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.createOrUpdateAudioAbbreviation(this.state.newItem);
    this.setState({
      newItem: { ...EMPTY_ITEM },
      isEditing: false,
    });
  };

  handleCancel = () => {
    this.setState({
      newItem: { ...EMPTY_ITEM },
      isEditing: false,
    });
  };

  handleImport = (importedItems) => {
    this.props.createOrUpdateMultipleAudioAbbreviations(importedItems);
  };

  moveToOrderIdx = (item, newOrderIdx) => {
    if (newOrderIdx === item.orderIdx) {
      return false;
    }
    const totalItems =
      this.props.audioAbbreviations?.pagination?.totalItemsInDataBase || 0;
    if (newOrderIdx >= 0 && newOrderIdx < totalItems) {
      this.props.createOrUpdateAudioAbbreviation({
        ...item,
        orderIdx: newOrderIdx,
      });
      return true;
    }
    return false;
  };

  handleMoveUp = (item) => this.moveToOrderIdx(item, item.orderIdx - 1);

  handleMoveDown = (item) => this.moveToOrderIdx(item, item.orderIdx + 1);

  handleMoveToNewOrderIdx = (item) => {
    this.setState({
      selectedItem: item,
      showMoveToNewOrderIdx: true,
    });
  };

  handleEdit = (item) => {
    this.setState({
      newItem: {
        text: item.text,
        replacement: item.replacement,
        type: item.type,
        orderIdx: item.orderIdx,
      },
      isEditing: true,
    });
  };

  handleDelete = (item) => {
    this.props.deleteAudioAbbreviation(item);
  };

  handleListenAudio = (item) => {
    this.audio.play(item.audioLink);
  };

  getItemOptions = (item) => {
    const options = [];
    const totalItems =
      this.props.audioAbbreviations?.pagination?.totalItemsInDataBase || 0;

    if (item.orderIdx > 0) {
      options.push({ name: 'Move up', event: () => this.handleMoveUp(item) });
    }
    if (item.orderIdx < totalItems - 1) {
      options.push({
        name: 'Move down',
        event: () => this.handleMoveDown(item),
      });
    }
    options.push({
      name: 'Move to index',
      event: () => this.handleMoveToNewOrderIdx(item),
    });
    options.push({ name: 'Edit', event: () => this.handleEdit(item) });
    options.push({ name: 'Delete', event: () => this.handleDelete(item) });
    options.push({
      name: 'Listen audio',
      event: () => this.handleListenAudio(item),
    });
    return options;
  };

  handlePageChange = (page) => {
    const totalPages =
      this.props.audioAbbreviations?.pagination?.totalPages || 1;
    if (page !== this.state.currentPage && page >= 1 && page <= totalPages) {
      this.setState({ currentPage: page });
      this.loadAudioAbbreviations(this.state.search, page);
    }
  };

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
  };

  render() {
    const { audioAbbreviations, isAudioAbbreviationsImportingLoading } =
      this.props;
    const items = audioAbbreviations?.data || [];
    const pagination = audioAbbreviations?.pagination || {};
    const areItemsLoading = !audioAbbreviations;

    return (
      <div className="audio-abbreviations-container">
        <div className="audio-abbreviations-body">
          <Row className="justify-content-between align-items-center">
            <Col>
              <h1>Audio abbreviations</h1>
            </Col>
          </Row>

          <hr />

          <AudioAbbreviationCsvUploader
            handleImport={this.handleImport}
            isLoading={isAudioAbbreviationsImportingLoading}
            parsedFile={this.state.parsedFile}
            setParsedFile={(file) => this.setState({ parsedFile: file })}
            inputFileRef={this.inputFileRef}
          />

          <hr />

          <AudioAbbreviationForm
            newItem={this.state.newItem}
            setNewItem={(item) => this.setState({ newItem: item })}
            isEditing={this.state.isEditing}
            areItemsLoading={areItemsLoading}
            handleSubmit={this.handleSubmit}
            handleCancel={this.handleCancel}
          />

          <hr />

          <AudioAbbreviationTable
            items={items}
            pagination={pagination}
            areItemsLoading={areItemsLoading}
            getItemOptions={this.getItemOptions}
            search={this.state.search}
            handleSearchChange={this.handleSearchChange}
            currentPage={this.state.currentPage}
            handlePageChange={this.handlePageChange}
            getAllAudioAbbreviations={this.props.getAllAudioAbbreviations}
          />

          <MoveToOrderIdxModal
            key={this.state.showMoveToNewOrderIdx}
            show={this.state.showMoveToNewOrderIdx}
            onHide={() => this.setState({ showMoveToNewOrderIdx: false })}
            item={this.state.selectedItem}
            moveToOrderIdx={(newOrderIdx) =>
              this.moveToOrderIdx(this.state.selectedItem, newOrderIdx)
            }
          />
        </div>
      </div>
    );
  }
}

function AudioAbbreviationForm({
  newItem,
  setNewItem,
  isEditing,
  areItemsLoading,
  handleSubmit,
  handleCancel,
}) {
  const isInputRequired =
    newItem.type === 'FREE_TEXT' || newItem.type === 'PHONEME';
  const areFieldsMissing =
    isInputRequired &&
    (newItem.replacement.trim().length === 0 ||
      newItem.text.trim().length === 0);

  const handleTypeChange = (value) => {
    setNewItem({
      ...newItem,
      replacement: '',
      type: value,
    });
  };

  return (
    <Form onSubmit={handleSubmit} className="mb-4">
      <Row className="mb-2">
        <Col>
          <Form.Group>
            <Form.Label>Text</Form.Label>
            <Form.Control
              type="text"
              value={newItem.text}
              disabled={isEditing}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  text: e.target.value,
                })
              }
              required
              placeholder="e.g. COMNAVSURFLANT"
            />
            <Form.Text className="text-muted">
              Specify a match to replace in questions&apos; text
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2">
        {ABBREVIATIONS_TYPES.map(({ text, value }) => (
          <Col xs="auto" key={value}>
            <Form.Check
              id={value}
              type="radio"
              label={text}
              name="abbreviationType"
              checked={newItem.type === value}
              onChange={() => handleTypeChange(value)}
              style={{ marginLeft: '3rem' }}
            />
          </Col>
        ))}
      </Row>
      {isInputRequired && (
        <Row className="mb-2">
          <Col>
            <Form.Group>
              <Form.Label>Replacement</Form.Label>
              <Form.Control
                type="text"
                value={newItem.replacement}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    replacement: e.target.value,
                  })
                }
                placeholder={
                  newItem.type === 'FREE_TEXT'
                    ? 'e.g. COM-NAV-SURF-LANT'
                    : `e.g. k@m'nev s3rfl{nt`
                }
              />
            </Form.Group>
            <Form.Text className="text-muted">
              Specify how you want the text-to-speech service to pronounce it
            </Form.Text>
          </Col>
        </Row>
      )}
      <Row
        className="justify-content-center"
        style={{ marginBottom: '-0.5rem' }}
      >
        <Col xs="auto">
          <Button
            type="submit"
            variant="primary"
            disabled={areItemsLoading || areFieldsMissing}
          >
            Save
          </Button>
        </Col>
        {isEditing && (
          <Col xs="auto">
            <Button variant="secondary" className="ms-2" onClick={handleCancel}>
              Cancel
            </Button>
          </Col>
        )}
      </Row>
    </Form>
  );
}

const FILE_HEADERS = ['orderIdx', 'text', 'type', 'replacement'];
function AudioAbbreviationCsvUploader({
  handleImport,
  isLoading,
  parsedFile,
  setParsedFile,
  inputFileRef,
}) {
  const handleFileChange = async (e) => {
    const newFile = e.target?.files?.[0];
    if (isCsvFile(newFile)) {
      const parsedNewFile = await parseCsvFile(newFile, FILE_HEADERS);
      const sanitizedNewFile = sanitizeAbbreviations(parsedNewFile);
      setParsedFile(sanitizedNewFile);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parsedFile) {
      handleImport(parsedFile);
      setParsedFile(null);
      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }
    }
  };

  const sanitizeAbbreviations = (parsedNewFile) =>
    parsedNewFile.map((row) => ({
      // Missing order indexes are handled by the backend
      // Recall that: Number('') = 0, Number(undefined) = NaN
      orderIdx:
        typeof row.orderIdx === 'undefined' || row.orderIdx === ''
          ? undefined
          : Number(row.orderIdx),
      text: row.text?.trim() || EMPTY_ITEM.text,
      replacement: row.replacement?.trim() || EMPTY_ITEM.replacement,
      type: row.type || EMPTY_ITEM.type,
    }));

  const getParsedFileCount = (type) =>
    parsedFile?.filter((a) => a.type === type).length || 0;

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <Form
      onSubmit={handleSubmit}
      className="d-flex flex-column align-items-center"
    >
      <Form.Group className="mb-3 w-100 text-center">
        <Form.Label>Create from CSV file</Form.Label>
        <Form.Text
          className="text-muted"
          style={{
            marginTop: '-0.25rem',
            marginBottom: '0.25rem',
            display: 'block',
          }}
        >
          Expected columns: {FILE_HEADERS.join(', ')}
        </Form.Text>
        <div className="mb-2">
          <a
            href="https://bluejacketeer.s3.amazonaws.com/files/AudioAbbreviations_Template.csv"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            <i className="fa fa-download" />
            Example Template
          </a>
        </div>
        <Form.Control
          type="file"
          accept=".csv"
          ref={inputFileRef}
          onChange={handleFileChange}
          className="mb-2"
          style={{
            maxWidth: '13.75rem',
            margin: '0 auto',
          }}
        />
        {parsedFile && (
          <Form.Text className="d-block mb-1 text-muted">
            {ABBREVIATIONS_TYPES.map(
              (type) => `${type.text}: ${getParsedFileCount(type.value)}`,
            ).join(', ')}
          </Form.Text>
        )}
      </Form.Group>
      <Button type="submit" disabled={!parsedFile}>
        Import
      </Button>
    </Form>
  );
}

function AudioAbbreviationTable({
  items,
  pagination,
  areItemsLoading,
  getItemOptions,
  search,
  handleSearchChange,
  currentPage,
  handlePageChange,
}) {
  const headerStyle = { position: 'sticky', top: '-5px', zIndex: 1 };

  if (areItemsLoading) {
    return <Spinner />;
  }

  const hasSearchTerm = search.trim().length > 0;
  const hasNoItems = items.length === 0;

  return (
    <>
      <Form.Text className="text-muted mb-2 d-block">
        Search by abbreviation text, replacement, or index number
      </Form.Text>
      <Form.Control
        type="text"
        className="form-control mw-50"
        placeholder="Search abbreviation"
        onChange={handleSearchChange}
        value={search}
      />
      <div style={{ maxHeight: '500px', overflowY: 'auto', marginTop: '1rem' }}>
        <Table responsive className="mt-2">
          <thead>
            <tr>
              <th style={headerStyle}>Index</th>
              <th style={headerStyle}>Abbreviation</th>
              <th style={headerStyle}>Replacement</th>
              <th style={headerStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {hasNoItems ? (
              <tr>
                <td colSpan="4" className="text-center text-muted py-4">
                  {hasSearchTerm
                    ? `No abbreviations found for "${search}"`
                    : 'No abbreviations available'}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <MemoizedAudioAbbreviationRow
                  key={item.id}
                  item={item}
                  getItemOptions={getItemOptions}
                />
              ))
            )}
          </tbody>
        </Table>
      </div>
      <PaginationControls
        pagination={pagination}
        onPageChange={handlePageChange}
        currentPage={currentPage}
      />
    </>
  );
}
function AudioAbbreviationRow({ item, getItemOptions }) {
  const spellOut = (text) =>
    text
      .split('')
      .filter((c) => c !== '-')
      .join('-');

  const truncate = (text) =>
    text.length > 8 ? `${text.substring(0, 8)}...` : text;

  return (
    <tr key={item.id}>
      <td>{item.orderIdx}</td>
      <td>{item.text}</td>
      <td>{item.replacement || spellOut(item.text)}</td>
      <td>
        <DropDown
          title={truncate(item.text)}
          options={getItemOptions(item)}
          key={item.id}
          style={{
            position: 'absolute',
            transform: 'translate(-95%, -12.5%)',
            zIndex: 100,
          }}
        />
      </td>
    </tr>
  );
}
const MemoizedAudioAbbreviationRow = React.memo(
  AudioAbbreviationRow,
  (prevProps, nextProps) =>
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.orderIdx === nextProps.item.orderIdx &&
    prevProps.item.text === nextProps.item.text &&
    prevProps.item.replacement === nextProps.item.replacement &&
    prevProps.item.type === nextProps.item.type &&
    prevProps.getItemOptions === nextProps.getItemOptions,
);

function MoveToOrderIdxModal({ show, onHide, item, moveToOrderIdx }) {
  const [newOrderIdx, setNewOrderIdx] = React.useState(0);
  const [isValid, setIsValid] = React.useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isNewOrderIdxValid = moveToOrderIdx(newOrderIdx);
    setIsValid(isNewOrderIdxValid);
    if (isNewOrderIdxValid) {
      setNewOrderIdx(newOrderIdx);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Move to index</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label>{`Enter a new index for the "${item.text}" abbreviation:`}</Form.Label>
            <Form.Control
              type="number"
              onChange={(e) => setNewOrderIdx(Number(e.target.value))}
            />
          </Form.Group>
          {!isValid && (
            <Form.Text className="text-danger">
              Please enter a valid index.
            </Form.Text>
          )}
          <div className="d-flex justify-content-center mt-3">
            <Button type="submit" variant="primary">
              Move
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function PaginationControls({ pagination, onPageChange, currentPage }) {
  if (!pagination || pagination.totalPages <= 1) {
    return null;
  }

  const { totalPages, totalItems, itemsPerPage, hasNextPage, hasPreviousPage } =
    pagination;

  const handlePageClick = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  // Calculate range of pages to show
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const pageNumbers = totalPages > 1 ? getPageNumbers() : [1];

  return (
    <div className="d-flex mt-3">
      <div className="small">
        {(currentPage - 1) * itemsPerPage + 1} -{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
      </div>
      <Pagination>
        <Pagination.Prev
          disabled={!hasPreviousPage}
          onClick={() => handlePageClick(currentPage - 1)}
        />
        {pageNumbers.map((page, index) =>
          page === '...' ? (
            <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
          ) : (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </Pagination.Item>
          ),
        )}

        <Pagination.Next
          disabled={!hasNextPage}
          onClick={() => handlePageClick(currentPage + 1)}
        />
      </Pagination>
    </div>
  );
}
